import { useEffect } from "react";
import { PoundSterling, ShieldCheck, Wrench, CalendarClock, TrendingUp, KeyRound, Check } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { Overline, PageHero, PrimaryLink, StatBlock } from "../components/Bits";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { IMAGES } from "../lib/site";

const BENEFITS = [
  { icon: PoundSterling, t: "One monthly rent", d: "A single agreed rent for the full length of your lease agreement." },
  { icon: CalendarClock, t: "Zero voids", d: "No empty months, no lost income, no chasing tenants for payment." },
  { icon: Wrench, t: "Full management", d: "We handle maintenance, repairs, compliance and every tenancy detail." },
  { icon: ShieldCheck, t: "Protected asset", d: "Regular professional inspections keep your property in top condition." },
  { icon: TrendingUp, t: "Transparent fees", d: "One simple 15% management fee — no letting fees, no hidden deductions." },
  { icon: KeyRound, t: "Returned in great shape", d: "At the end of term your property comes back well-maintained and ready." },
];

const STEPS = [
  { n: "01", t: "Quick chat", d: "Tell us about your property and what you're looking for." },
  { n: "02", t: "Property review", d: "We assess and agree a monthly rent that works for you." },
  { n: "03", t: "Sign & relax", d: "We take over management on a long-term agreement." },
  { n: "04", t: "Hands-off", d: "We manage everything throughout the term — completely hands-off." },
];

const COVERS = [
  "Full tenant management & day-to-day communication",
  "Rent collection and clear monthly statements",
  "Routine inspections and condition reporting",
  "Repairs & maintenance coordination",
  "Compliance, safety checks and certification",
  "A dedicated point of contact for your property",
];

const FAQS = [
  {
    q: "What is rent-to-rent?",
    a: "We take on your property on a long-term lease and manage it end to end. You receive one agreed monthly rent while we look after the tenancy, maintenance and compliance on your behalf.",
  },
  {
    q: "How much is your management fee?",
    a: "One simple, transparent 15% management fee. There are no letting fees and no hidden extras — the fee covers full tenant management, rent collection, inspections, repairs coordination and compliance.",
  },
  {
    q: "How long are your agreements?",
    a: "Our agreements typically run for 3 to 5 years, giving you long-term certainty and a genuinely hands-off arrangement for the whole term.",
  },
  {
    q: "Who handles maintenance and repairs?",
    a: "We do. Our team manages day-to-day maintenance and coordinates any repairs throughout the agreement, keeping your property in excellent condition.",
  },
  {
    q: "Will my property be used for supported housing?",
    a: "Where a property is suitable, we may place tenants through supported housing partnerships with local authorities. Every home is managed to a compliance-first, inspection-backed standard.",
  },
  {
    q: "What condition will my property be returned in?",
    a: "Your property is returned to you well-maintained and ready at the end of the agreed term, having been professionally managed and regularly inspected throughout.",
  },
  {
    q: "Which areas do you cover?",
    a: "We operate right across the North West of England, including Manchester, Liverpool, Salford, Bolton, Preston, Blackburn, Wigan and the surrounding towns.",
  },
];

export default function Landlords() {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-jsonld";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(script);
    return () => {
      const ex = document.getElementById("faq-jsonld");
      if (ex) ex.remove();
    };
  }, []);

  return (
    <div data-testid="landlords-page">
      <PageHero
        overline="For landlords"
        titleLines={["Rent-to-rent,", "done properly."]}
        subtitle="Hand us the keys and we'll take care of the rest — one agreed monthly rent, no void-period hassle, and a property looked after to an inspection-backed standard."
        image={IMAGES.keys}
      />

      <section className="container-x -mt-6 relative z-10">
        <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {[
            { v: "3–5 yrs", l: "Lease term" },
            { v: "15%", l: "Management fee" },
            { v: "0", l: "Voids to manage" },
            { v: "100%", l: "Fully managed" },
          ].map((s, i) => (
            <StaggerItem key={i}><StatBlock value={s.v} label={s.l} testid={`landlord-stat-${i}`} /></StaggerItem>
          ))}
        </Stagger>
      </section>

      <section className="container-x py-24 lg:py-32">
        <Reveal><Overline>Why landlords choose us</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 max-w-2xl font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">
            Everything a landlord wants. Nothing they dread.
          </h2>
        </Reveal>
        <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {BENEFITS.map((b, i) => (
            <StaggerItem key={i}>
              <div className="card-surface h-full rounded-3xl p-8 hover:border-amber/30 transition-colors" data-testid={`landlord-benefit-${i}`}>
                <b.icon className="h-8 w-8 text-amber" />
                <h3 className="mt-5 font-display font-bold text-xl text-white">{b.t}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{b.d}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      <section className="relative py-24 lg:py-32 overflow-hidden border-y border-white/10 bg-surface/30">
        <div className="container-x">
          <Reveal><Overline>How it works</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl tracking-tight text-white">From keys to income in four steps.</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="relative h-full rounded-3xl border border-white/10 p-8" data-testid={`landlord-step-${s.n}`}>
                  <span className="font-mono text-3xl text-amber">{s.n}</span>
                  <h3 className="mt-4 font-display font-bold text-xl text-white">{s.t}</h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOUR 15% COVERS */}
      <section className="container-x py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Reveal><Overline>Simple, transparent pricing</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">
                What your 15% covers.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-slate-300 leading-relaxed max-w-md">
                One straightforward management fee — no letting fees, no hidden extras. Here's
                everything we handle so you don't have to.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Stagger className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
              {COVERS.map((c, i) => (
                <StaggerItem key={i}>
                  <div className="flex items-start gap-3" data-testid={`fee-cover-${i}`}>
                    <span className="mt-0.5 grid place-items-center h-6 w-6 rounded-full bg-amber/15 shrink-0">
                      <Check className="h-3.5 w-3.5 text-amber" />
                    </span>
                    <span className="text-slate-200">{c}</span>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      {/* LANDLORD FAQ */}
      <section className="container-x py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <Reveal><Overline>Landlord FAQ</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">
                Questions, answered.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-slate-300 leading-relaxed max-w-sm">
                Everything landlords usually ask before handing us the keys. Still unsure?{" "}
                <a href="mailto:contact@insightproperties.co.uk" className="text-amber hover:text-amber-soft transition-colors">
                  Drop us an email.
                </a>
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <Accordion type="single" collapsible className="w-full" data-testid="landlord-faq">
                {FAQS.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                    <AccordionTrigger
                      data-testid={`faq-trigger-${i}`}
                      className="text-base sm:text-lg font-display font-semibold text-white hover:no-underline py-5"
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent data-testid={`faq-content-${i}`} className="text-slate-400 text-base leading-relaxed">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="container-x py-28 lg:py-40">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] card-surface p-10 lg:p-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-white leading-[0.95]">
                Turn your property into a hands-off investment.
              </h2>
              <p className="mt-6 text-slate-300 max-w-md">Fair fees, no hassle, no stress. Let's talk about your property today.</p>
              <div className="mt-8"><PrimaryLink to="/contact" testid="landlord-cta">Get a rent offer</PrimaryLink></div>
            </div>
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
              <img src={IMAGES.handshake} alt="Landlord partnership" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/50 to-transparent" />
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
